import {
  IconArrowDown,
  IconArrowUp,
  IconMicrophone,
  IconPlayerStopFilled,
  IconPlus,
  IconUserCheck,
  IconX,
} from '@tabler/icons-react';
import { type Dispatch, type SetStateAction, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCombobox } from '@/hooks';
import { logger } from '@/lib/logger';
import type { Patient } from '@/types/api';
import { splitPlanSteps } from '@/utils/planSteps';
import { patientsApi } from '../../patients/api';
import type { OfferInput } from '../api';
import { matchPatient } from '../matchPatient';
import { useFieldDictation } from '../useFieldDictation';

export type OfferDraft = Omit<OfferInput, 'totalRsd' | 'dentistId'> & {
  /** Digits as typed; '' while empty. */
  total: string;
  /** Name of the linked patient, to show it. */
  linkedName: string | null;
};

export const EMPTY_DRAFT: OfferDraft = {
  patientId: null,
  patientName: '',
  linkedName: null,
  steps: [],
  total: '',
  note: null,
};

/** The server's limits (docs/API.md), so a draft never fails on Save. */
const MAX_STEPS = 40;
const MAX_STEP_LENGTH = 500;
const MAX_TOTAL_RSD = 100_000_000;

/** The offer the draft describes, or null while something required is missing or too long. */
export function draftToInput(d: OfferDraft): Omit<OfferInput, 'dentistId'> | null {
  const steps = d.steps.map((s) => s.trim()).filter(Boolean);
  const name = d.patientName.trim();
  if (!(name && steps.length && /^\d{1,9}$/.test(d.total))) return null;
  if (steps.length > MAX_STEPS || steps.some((s) => s.length > MAX_STEP_LENGTH)) return null;
  if (Number(d.total) > MAX_TOTAL_RSD || name.length > 200) return null;
  return {
    patientId: d.patientId,
    patientName: name,
    steps,
    totalRsd: Number(d.total),
    note: d.note?.trim() || null,
  };
}

function MicButton({
  dictation,
  label,
}: {
  dictation: ReturnType<typeof useFieldDictation>;
  label: string;
}) {
  if (!dictation.supported) return null;
  return (
    <button
      type="button"
      className={`icon-btn offer-mic ${dictation.listening ? 'offer-mic--live' : ''}`}
      aria-pressed={dictation.listening}
      aria-label={label}
      title={label}
      onClick={dictation.toggle}
    >
      {dictation.listening ? <IconPlayerStopFilled size={20} /> : <IconMicrophone size={22} />}
    </button>
  );
}

const fullName = (p: Patient) => `${p.lastName} ${p.firstName}`;
const linked = (p: Patient): Partial<OfferDraft> => ({
  patientId: p.id,
  patientName: fullName(p),
  linkedName: fullName(p),
});

/** The name field: dictated or typed, matched against the patient list, else free text. */
function PatientField({
  draft,
  onChange,
  dictation,
}: {
  draft: OfferDraft;
  onChange: (patch: Partial<OfferDraft>) => void;
  dictation: ReturnType<typeof useFieldDictation>;
}) {
  const { t } = useTranslation();
  const [found, setFound] = useState<Patient[]>([]);
  const [closed, setClosed] = useState(false);
  const query = draft.patientId ? '' : draft.patientName.trim();
  const shown = !(draft.patientId || closed) ? found : [];
  const combo = useCombobox({
    id: 'offer-patient-suggestions',
    items: shown,
    onPick: (p) => onChange(linked(p)),
    onClose: () => setClosed(true),
    onOpen: () => setClosed(false),
  });

  useEffect(() => {
    if (query.length < 2) {
      setFound([]);
      return;
    }
    let live = true;
    const timer = setTimeout(() => {
      patientsApi
        .list(query, 5)
        .then((list) => live && setFound(list))
        .catch(() => live && setFound([]));
    }, 250);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [query]);

  return (
    <div className="field">
      <label className="field__label" htmlFor="offer-patient">
        {t('offers.patient')}
      </label>
      <div className="row">
        <input
          id="offer-patient"
          className="input grow"
          maxLength={200}
          value={dictation.listening && dictation.interim ? dictation.interim : draft.patientName}
          placeholder={t('offers.patientHint')}
          autoComplete="off"
          {...combo.input}
          onChange={(e) => {
            onChange({ patientId: null, linkedName: null, patientName: e.target.value });
            setClosed(false);
            combo.reset();
          }}
        />
        <MicButton dictation={dictation} label={t('offers.dictateName')} />
      </div>
      {draft.patientId ? (
        <span className="badge badge--approved offer-linked">
          <IconUserCheck size={14} aria-hidden="true" />
          {t('offers.linked', { name: draft.linkedName })}
        </span>
      ) : (
        query && <span className="small muted">{t('offers.freeText')}</span>
      )}
      {shown.length > 0 && (
        <div
          {...combo.list}
          role="listbox"
          className="offer-suggest"
          aria-label={t('offers.suggestions')}
        >
          {shown.map((p, i) => (
            <div
              key={p.id}
              {...combo.option(i, p)}
              className={`autocomplete__option ${i === combo.active ? 'is-active' : ''}`}
            >
              <strong>{fullName(p)}</strong>
              {p.birthDate && <span className="small muted">{p.birthDate.slice(0, 4)}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** The plan: dictated phrases become steps; every step can be corrected, moved or removed. */
function StepsField({
  steps,
  onChange,
  dictation,
}: {
  steps: string[];
  onChange: (steps: string[]) => void;
  dictation: ReturnType<typeof useFieldDictation>;
}) {
  const { t } = useTranslation();
  const list = useRef<HTMLOListElement>(null);
  // The step to focus once it is rendered (a new one: typing goes on at once);
  // checked after every render, which is cheap.
  const focusNext = useRef<number | null>(null);
  useEffect(() => {
    const i = focusNext.current;
    if (i === null) return;
    focusNext.current = null;
    list.current?.querySelectorAll('input')[i]?.focus();
  });
  const insert = (at: number) => {
    if (steps.length >= MAX_STEPS) return;
    focusNext.current = at;
    onChange([...steps.slice(0, at), '', ...steps.slice(at)]);
  };
  const set = (i: number, value: string) => onChange(steps.map((s, k) => (k === i ? value : s)));
  const move = (i: number, by: number) => {
    const next = [...steps];
    const [step] = next.splice(i, 1);
    next.splice(i + by, 0, step ?? '');
    onChange(next);
  };
  return (
    <div className="field">
      <div className="row">
        <span className="field__label grow">{t('offers.plan')}</span>
        <MicButton dictation={dictation} label={t('offers.dictatePlan')} />
      </div>
      {steps.length > 0 && (
        <ol className="offer-steps" ref={list}>
          {steps.map((step, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: steps are plain text edited in place
            <li key={i} className="offer-steps__item">
              <span className="offer-steps__no">{i + 1}.</span>
              <input
                className="input grow"
                value={step}
                maxLength={MAX_STEP_LENGTH}
                aria-label={t('offers.step', { n: i + 1 })}
                onChange={(e) => set(i, e.target.value)}
                onKeyDown={(e) => {
                  // Enter: the next step right below, not a form submit; an empty one stays alone.
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  if (step.trim()) insert(i + 1);
                }}
              />
              <button
                type="button"
                className="icon-btn icon-btn--sm"
                disabled={i === 0}
                onClick={() => move(i, -1)}
                aria-label={t('offers.up')}
              >
                <IconArrowUp size={16} />
              </button>
              <button
                type="button"
                className="icon-btn icon-btn--sm"
                disabled={i === steps.length - 1}
                onClick={() => move(i, 1)}
                aria-label={t('offers.down')}
              >
                <IconArrowDown size={16} />
              </button>
              <button
                type="button"
                className="icon-btn icon-btn--sm"
                onClick={() => onChange(steps.filter((_, k) => k !== i))}
                aria-label={t('offers.removeStep')}
              >
                <IconX size={16} />
              </button>
            </li>
          ))}
        </ol>
      )}
      {dictation.listening && (
        <p className="small muted offer-interim" aria-live="polite">
          {dictation.interim || t('offers.listening')}
        </p>
      )}
      {dictation.error && <p className="small error-text">{dictation.error}</p>}
      {steps.length >= MAX_STEPS && (
        <p className="small muted">{t('offers.maxSteps', { count: MAX_STEPS })}</p>
      )}
      <button
        type="button"
        className="btn btn--sm"
        disabled={steps.length >= MAX_STEPS}
        onClick={() => insert(steps.length)}
      >
        <IconPlus size={16} /> {t('offers.addStep')}
      </button>
    </div>
  );
}

type Props = {
  draft: OfferDraft;
  onChange: Dispatch<SetStateAction<OfferDraft>>;
  busy: boolean;
  onSave: () => void;
  onCancel?: () => void;
};

/** Patient, plan, total and note of an offer. */
export function OfferEditor({ draft, onChange, busy, onSave, onCancel }: Props) {
  const { t } = useTranslation();
  const patch = (p: Partial<OfferDraft>) => onChange((d) => ({ ...d, ...p }));

  // A dictated name is looked up; an exact match links the patient, otherwise it stays free text.
  const name = useFieldDictation((text) => {
    patch({ patientId: null, linkedName: null, patientName: text });
    patientsApi
      .list(text, 10)
      .then((list) => {
        const match = matchPatient(text, list);
        // Only while the field still holds what was said.
        if (match) onChange((d) => (d.patientName === text ? { ...d, ...linked(match) } : d));
      })
      .catch((err) => logger.warn('offer: patient lookup failed', err));
  });
  // Each phrase of the plan becomes one or more steps, after the ones already there.
  const plan = useFieldDictation((text) =>
    onChange((d) => ({
      ...d,
      steps: [...d.steps.filter((s) => s.trim()), ...splitPlanSteps(text)].slice(0, MAX_STEPS),
    }))
  );
  // One microphone at a time.
  const nameMic = {
    ...name,
    toggle: () => {
      plan.stop();
      name.toggle();
    },
  };
  const planMic = {
    ...plan,
    toggle: () => {
      name.stop();
      plan.toggle();
    },
  };

  const valid = draftToInput(draft) !== null;
  return (
    <section className="card stack offer-editor">
      <PatientField draft={draft} onChange={patch} dictation={nameMic} />
      <StepsField steps={draft.steps} onChange={(steps) => patch({ steps })} dictation={planMic} />
      <div className="field">
        <label className="field__label" htmlFor="offer-total">
          {t('offers.total')}
        </label>
        <div className="row">
          <input
            id="offer-total"
            className="input offer-total"
            inputMode="numeric"
            value={draft.total}
            placeholder="0"
            onChange={(e) => patch({ total: e.target.value.replace(/\D+/g, '').slice(0, 9) })}
            aria-invalid={Number(draft.total) > MAX_TOTAL_RSD}
          />
          <span className="muted">RSD</span>
        </div>
      </div>
      <div className="field">
        <label className="field__label" htmlFor="offer-note">
          {t('offers.note')}
        </label>
        <textarea
          id="offer-note"
          className="input"
          rows={3}
          maxLength={2000}
          value={draft.note ?? ''}
          onChange={(e) => patch({ note: e.target.value })}
        />
      </div>
      <p className="small muted">{t('offers.validity')}</p>
      <div className="row">
        {onCancel && (
          <button type="button" className="btn" onClick={onCancel}>
            {t('common.cancel')}
          </button>
        )}
        <button
          type="button"
          className="btn btn--dark grow"
          disabled={busy || !valid}
          onClick={() => {
            name.stop();
            plan.stop();
            onSave();
          }}
        >
          {t('common.save')}
        </button>
      </div>
    </section>
  );
}
